import { Category } from 'models';
import { baseApi } from './baseApi';
import {
  TAG_TYPES,
  QueryParams,
  CreateCategoryRequest,
  ReplaceResponse,
  CheckUsedResponse,
  ReplaceRequest,
} from 'types';
import { getQueryParams } from 'utils';
const URL_CATEGORY = 'categories';

export const categoryApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCategories: builder.query<
      {
        data: {
          rows: Category[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_CATEGORY, method: 'GET' };
        return {
          url: `${URL_CATEGORY}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Category, id })),
              { type: TAG_TYPES.Category, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Category, id: 'LIST' }];
      },
    }),

    getByIdCategory: builder.query<{ data: Category; success: boolean }, { id: string; params?: QueryParams }>({
      query: ({ id, params }) => ({
        url: `${URL_CATEGORY}/${id}`,
        method: 'GET',
        params: params || {},
      }),
      providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Category, id }],
    }),

    createCategory: builder.mutation<{ data: Category; success: boolean }, CreateCategoryRequest>({
      query: (data) => {
        const payload = { ...data };
        return {
          url: URL_CATEGORY,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: [
        { type: TAG_TYPES.Category, id: 'LIST' },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),

    updateCategory: builder.mutation<{ data: Category; success: boolean }, CreateCategoryRequest>({
      query: ({ id, ...patch }) => {
        const payload = { ...patch };

        return {
          url: `${URL_CATEGORY}/${id}`,
          method: 'PUT',
          data: payload,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Category, id },
        { type: TAG_TYPES.Category, id: 'LIST' },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),

    deleteCategory: builder.mutation<{ data: Category; success: boolean }, string>({
      query: (id) => ({
        url: `${URL_CATEGORY}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Category, id },
        { type: TAG_TYPES.Category, id: 'LIST' },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),
    checkCategoryUsage: builder.query<CheckUsedResponse, string>({
      query: (id) => ({
        url: `${URL_CATEGORY}/${id}/check-used`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Category, id: `CHECK_USED_${id}` }],
    }),

    deactivateCategory: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_CATEGORY}/${id}/deactivate`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Category, id },
        { type: TAG_TYPES.Category, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),

    deleteCategoryWithReplacement: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_CATEGORY}/${id}/delete`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Category, id },
        { type: TAG_TYPES.Category, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllCategoriesQuery,
  useLazyGetAllCategoriesQuery,
  useGetByIdCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useLazyCheckCategoryUsageQuery,
  useDeactivateCategoryMutation,
  useDeleteCategoryWithReplacementMutation,
} = categoryApiService;
